import { Link } from 'react-router-dom';
import ResearchPageHeader from '../components/ResearchPageHeader';
import './Research.css';
import './ResearchBuyBox.css';

const BUYBOX_HEADER_STATS = {
  label: 'Deal Criteria',
  centered: true,
  items: [
    { value: '75%', label: 'All In at' },
    { value: '💵', label: 'Cash Flow +' },
    { value: '📍', label: 'Area Risk' },
  ],
};

const SOFTWARE_CONTEXT_ITEMS = [
  'Market research helps us choose better areas.',
  'Deal analysis helps us prove the numbers.',
  'Field inspections help us document the property.',
  'Scopes of work help us define the plan.',
  'Owner-purchased materials help us control costs.',
  'Labor-only bids help us compare contractors fairly.',
  'Milestone verification helps us release payment only after proof.',
  'Tenant repair workflows help us manage maintenance fairly.',
  'Move-in and move-out documentation helps us protect deposits with evidence.',
  'Scorecards help us send more work to the people who perform well.',
  'Clean financial controls help us scale without giving away control of money, data, repairs, vendors, documentation, or decision-making.',
];

const BUYBOX_SECTIONS = [
  {
    id: 'criteria',
    kicker: 'Section 1',
    title: 'BRRRR Deal Criteria',
    intro: 'These are the non-negotiable filters every deal must clear before we deploy capital, attention, and execution capacity.',
    cards: [
      {
        step: '01',
        icon: 'pi-home',
        title: '75% ARV Target',
        standard: 'Be all in at or below 75% of after-repair value.',
        detail: 'Acquisition, rehab, financing, closing, holding, and contingency costs should all still fit inside the refinance window.',
      },
      {
        step: '02',
        icon: 'pi-chart-line',
        title: 'Positive Cash Flow',
        standard: 'Produce positive cash flow after the refinance closes and seasons.',
        detail: 'Underwrite rent, debt service, taxes, insurance, repairs, vacancy, management, utilities, and reserves before approving the deal.',
      },
      {
        step: '03',
        icon: 'pi-map-marker',
        title: 'Low-Headache Area',
        standard: 'Choose markets and blocks that reduce avoidable operating friction.',
        detail: 'Neighborhood quality, landlord rules, insurance pressure, crime, schools, vendors, and tenant fit all shape the true workload.',
      },
    ],
  },
  {
    id: 'skillsets',
    kicker: 'Section 2',
    title: 'Core Skills Needed',
    intro: 'These are the repeatable operating systems our software should sharpen before we source, rehab, refinance, and scale.',
    cards: [
      {
        step: '01',
        icon: 'pi-search',
        title: 'Find & Analyze Deals',
        standard: 'Build repeatable systems for finding deals where numbers work.',
        detail: 'Systematize sourcing, rent comps, ARV support, repair budgets, and all-in cost tracking before sending offers out.',
      },
      {
        step: '02',
        icon: 'pi-wrench',
        title: 'Manage Rehab Remotely',
        standard: 'Build rehab systems that stay reliable without constant local oversight.',
        detail: 'Use inspections, scopes, photo proof, vendor follow-up, material tracking, and punch lists to control execution quality.',
      },
      {
        step: '03',
        icon: 'pi-sync',
        title: 'Refinance & Recycle Capital',
        standard: 'Build refinance workflows that recycle capital into the next deal.',
        detail: 'Track lender requirements, appraisal support, rehab documentation, cost basis, seasoning, and final returns through closing cleanly.',
      },
    ],
  },
  {
    id: 'owner-control',
    kicker: 'Section 3',
    title: 'Owner-Controlled Operating Model',
    intro: 'We own the property, so we own the money, the data, the approval process, and the documentation standards. We delegate operations, but not financial control.',
    cards: [
      {
        step: '01',
        icon: 'pi-wallet',
        title: 'Owner-Controlled Finances',
        standard: 'Tenants pay directly through our app to our business, not to the property manager first.',
        detail: 'Cash collection, ledger visibility, payment timing, and audit history should sit inside the owner-controlled system from day one.',
      },
      {
        step: '02',
        icon: 'pi-briefcase',
        title: 'PMs Manage Operations, Not Owner Cash',
        standard: 'Property managers handle tenant communication, coordination, lease process, and local issues, but they operate inside our system.',
        detail: 'The PM is a field operator and workflow executor, not the system of record for owner money, approvals, or reporting standards.',
      },
      {
        step: '03',
        icon: 'pi-database',
        title: 'Standardized Categories, Clean Data & Agreement',
        standard: 'Every rent payment, fee, repair, vendor invoice, and deposit deduction is categorized, and we control the agreement.',
        detail: 'Standard data structure, approval rules, and documentation requirements make every property easier to audit, compare, and improve over time.',
      },
    ],
  },
  {
    id: 'repair-controls',
    kicker: 'Section 4',
    title: 'Repair & Vendor Controls',
    intro: 'This is the PM and vendor protection system. Local operators can coordinate the work, but vendor selection, approval standards, documentation, and payout controls stay inside the owner-controlled workflow.',
    cards: [
      {
        step: '01',
        icon: 'pi-users',
        title: 'Approved Vendor Network',
        standard: 'Property managers select vendors only from our approved list unless owner approval is given.',
        detail: 'Preferred vendors should already be screened for pricing, quality, response time, licensing, insurance, and accountability before they receive recurring work.',
      },
      {
        step: '02',
        icon: 'pi-file-edit',
        title: 'Repair Documentation Required',
        standard: 'Repairs require photos, descriptions, invoices, and approval trails.',
        detail: 'Every job should leave a clean record of scope, cost, supporting evidence, and who approved it so the owner can audit the decision later.',
      },
      {
        step: '03',
        icon: 'pi-eye',
        title: 'Field Inspector Verification',
        standard: 'Rehab and field inspectors verify work quality and completion before payment when needed.',
        detail: 'Independent verification reduces fake completion, rushed closeouts, and avoidable rework before owner funds are released.',
      },
    ],
  },
  {
    id: 'deposit-fairness',
    kicker: 'Section 5',
    title: 'Security Deposit Fairness',
    intro: 'Security deposits should be handled with documented fairness. The system should protect owners from undocumented damage claims while also protecting tenants from arbitrary deductions.',
    cards: [
      {
        step: '01',
        icon: 'pi-camera',
        title: 'Move-In Baseline Photos',
        standard: 'Same-angle photos and walkthrough videos are taken when the tenant moves in.',
        detail: 'The move-in record creates the baseline for condition, cleanliness, and pre-existing wear before occupancy begins.',
      },
      {
        step: '02',
        icon: 'pi-images',
        title: 'Move-Out Comparison',
        standard: 'The same photos are taken at move-out to compare actual condition changes.',
        detail: 'Matched photo angles and consistent walkthroughs make it easier to separate normal wear from real tenant-caused damage.',
      },
      {
        step: '03',
        icon: 'pi-shield',
        title: 'No Documentation, No Deduction',
        standard: 'Deposit deductions must be supported by before-and-after evidence, itemized costs, and clear reasoning.',
        detail: 'If the file cannot prove the claim cleanly, the system should treat the deduction as weak, risky, or invalid.',
      },
    ],
  },
  {
    id: 'remote-rehab',
    kicker: 'Section 6',
    title: 'Remote Rehab Command Center',
    intro: 'Out-of-state investing requires real-time visibility, controlled access, documented conditions, and local accountability. The command center should help us see what is happening at the property, control who can enter, verify progress, and make decisions without depending on guesswork.',
    cards: [
      {
        step: '01',
        icon: 'pi-lock',
        title: 'Site Control & Visibility',
        standard: 'Secure the property and create visibility before rehab begins.',
        detail: 'Smart locks, lockboxes, cameras, internet and router setup, access logs, entry rules, and field updates help us know who entered, when they entered, why they entered, what happened on site, and whether the property stayed secure.',
      },
      {
        step: '02',
        icon: 'pi-camera',
        title: 'Field Evidence Before Scope',
        standard: 'Rehab field inspectors document the property before contractors bid.',
        detail: 'Photos, videos, measurements, room notes, system notes, exterior notes, damage notes, and value-add opportunities should create the factual foundation for the scope of work.',
      },
      {
        step: '03',
        icon: 'pi-desktop',
        title: 'Local Eyes, Owner Visibility',
        standard: 'Field inspectors become our trusted local eyes without replacing the property manager or contractor.',
        detail: 'Their role is to document, verify, and report so the owner can make better decisions from out of state with less guesswork.',
      },
    ],
  },
  {
    id: 'material-bid-control',
    kicker: 'Section 7',
    title: 'Material & Bid Control',
    intro: 'Material decisions and contractor bids should be structured in a way that protects the budget, improves comparison, and reduces hidden markup.',
    cards: [
      {
        step: '01',
        icon: 'pi-shopping-cart',
        title: 'Owner-Purchased Materials',
        standard: 'Whenever practical, materials are selected and purchased through the owner-controlled system.',
        detail: 'This gives us better control over material cost, quality, receipts, product consistency, and historical pricing across multiple rehabs.',
      },
      {
        step: '02',
        icon: 'pi-wallet',
        title: 'Labor-Only Contractor Bids',
        standard: 'Contractors should bid labor separately from materials whenever possible.',
        detail: 'Labor-only bids make it easier to compare contractors fairly, reduce hidden material markup, and understand the true cost of each rehab component.',
      },
      {
        step: '03',
        icon: 'pi-calculator',
        title: 'Measured Material Planning',
        standard: 'Material estimates should come from field measurements, not vague guesses.',
        detail: 'Flooring, paint, drywall, trim, cabinets, counters, tile, doors, windows, and other materials should be tied to documented measurements and property-specific scope data.',
      },
    ],
  },
  {
    id: 'milestones-payments',
    kicker: 'Section 8',
    title: 'Milestones, Payments & Proof',
    intro: 'Payments should be tied to verified progress, not vague promises or unchecked completion claims.',
    cards: [
      {
        step: '01',
        icon: 'pi-flag',
        title: 'Milestone-Based Completion',
        standard: 'Rehab work should be broken into clear milestones.',
        detail: 'Each milestone should define the work expected, the proof required, the payment amount, and the conditions that must be met before the job moves forward.',
      },
      {
        step: '02',
        icon: 'pi-check-circle',
        title: 'Verification Before Payment',
        standard: 'Contractor payments should require documentation before funds are released.',
        detail: 'Photos, videos, notes, invoices, inspector verification, and owner approval should support payment decisions whenever the work is material or risk-sensitive.',
      },
      {
        step: '03',
        icon: 'pi-check-square',
        title: 'Inspector Sign-Off Before Payment',
        standard: 'Contractor payout is not released until the required work is documented, reviewed, and checked off by the field inspector.',
        detail: 'Inspector sign-off, progress photos, correction requests, and final verification help ensure we pay for completed work, not promises, rushed closeouts, or undocumented claims.',
      },
    ],
  },
  {
    id: 'scorecards',
    kicker: 'Section 9',
    title: 'Scorecards & Accountability',
    intro: 'The system should reward the people who protect the business and reveal the people who create risk.',
    cards: [
      {
        step: '01',
        icon: 'pi-star',
        title: 'Contractor Reliability Scores',
        standard: 'Contractors should be scored on quality, timeliness, communication, price accuracy, documentation, rework, callbacks, and milestone completion.',
        detail: 'The best contractors should receive more opportunities because their performance is proven by data.',
      },
      {
        step: '02',
        icon: 'pi-search',
        title: 'Inspector Quality Scores',
        standard: 'Inspectors should be scored on photo quality, video quality, measurement accuracy, note completeness, responsiveness, and usefulness of documentation.',
        detail: 'The most reliable field inspectors should receive more assignments and greater responsibility over time.',
      },
      {
        step: '03',
        icon: 'pi-briefcase',
        title: 'Property Manager Accountability',
        standard: 'Property managers should be evaluated on communication, maintenance handling, documentation quality, cost control, tenant coordination, fee transparency, and compliance with our system.',
        detail: 'The right PMs will work inside our process. The wrong PMs will self-select out.',
      },
    ],
  },
  {
    id: 'data-scaling',
    kicker: 'Section 10',
    title: 'Data-Driven Scaling',
    intro: 'Every property should make the next property easier to analyze, rehab, rent, repair, and manage.',
    cards: [
      {
        step: '01',
        icon: 'pi-database',
        title: 'Clean Data From the Source',
        standard: 'Data should be created cleanly inside the workflow instead of reconstructed later from messy PDFs, inconsistent PM statements, or unclear repair notes.',
        detail: 'Standard categories, required documentation, approval trails, and connected transactions make reporting and analysis more accurate.',
      },
      {
        step: '02',
        icon: 'pi-chart-line',
        title: 'Forecast Future Costs',
        standard: 'Past repair, rehab, vendor, tenant, PM, and property data should help forecast future cash needs.',
        detail: 'The more properties we operate, the better we should become at estimating repairs, reserves, vacancy, maintenance, labor, materials, and cash flow risk.',
      },
      {
        step: '03',
        icon: 'pi-sync',
        title: 'Build Repeatable Systems',
        standard: 'The business should become more scalable with every deal.',
        detail: 'Each inspection, repair, invoice, vendor score, deposit decision, refinance, and PM interaction should improve the operating system and reduce future uncertainty.',
      },
    ],
  },
  {
    id: 'budget-accuracy',
    kicker: 'Section 11',
    title: 'Budget Accuracy & Material Standards',
    intro: 'Every deal should compare projected costs against actual costs so our buying, rehab, material, and refinance assumptions become more accurate over time.',
    cards: [
      {
        step: '01',
        icon: 'pi-chart-bar',
        title: 'Budget vs. Actual Tracking (Cost Variance)',
        standard: 'Track estimated costs against actual costs from purchase through refinance.',
        detail: 'Purchase price, closing costs, rehab labor, materials, permits, utilities, insurance, holding costs, lender fees, appraisal costs, and contingency usage should be compared against the original deal analysis so we can see where the deal stayed on plan and where it drifted.',
      },
      {
        step: '02',
        icon: 'pi-box',
        title: 'Preferred Material Standards',
        standard: 'Use materials that are proven, repeatable, budget-friendly, and durable across multiple rehabs.',
        detail: 'Flooring, paint, fixtures, cabinets, counters, doors, trim, appliances, and other common materials should be tracked by cost, quality, availability, install performance, tenant durability, and how well they fit the budget.',
      },
      {
        step: '03',
        icon: 'pi-refresh',
        title: 'Projection Feedback Loop',
        standard: 'Actual costs should improve future estimates.',
        detail: 'Every completed purchase, material order, labor bid, repair, change order, and rehab invoice should feed back into future underwriting so our projected costs become closer to reality over time.',
      },
    ],
  },
  {
    id: 'tenant-screening',
    kicker: 'Section 12',
    title: 'Tenant Screening & Leasing Standards',
    intro: 'Tenant placement should follow a consistent, documented, and owner-controlled process so we do not depend on random property manager judgment or undocumented tenant approval decisions.',
    cards: [
      {
        step: '01',
        icon: 'pi-file',
        title: 'Standard Application Workflow',
        standard: 'Every applicant should apply through the tenant portal.',
        detail: 'Applications, income documents, identification, rental history, screening consent, application status, and decision history should be captured in one system so the tenant approval process is clear and auditable.',
      },
      {
        step: '02',
        icon: 'pi-check-circle',
        title: 'Written Screening Criteria',
        standard: 'Tenant decisions should be based on written criteria, not personal preference or inconsistent judgment.',
        detail: 'Income, rental history, employment verification, credit, eviction history, occupancy rules, pet policies, and manual review rules should be applied consistently for the property and ownership entity.',
      },
      {
        step: '03',
        icon: 'pi-shield',
        title: 'Owner-Controlled Leasing Approval',
        standard: 'Property managers can review, communicate, and recommend, but final leasing standards stay inside the owner-controlled system.',
        detail: 'Approvals, conditional approvals, denials, lease terms, deposits, move-in charges, and notices should follow the documented workflow before a tenant is placed.',
      },
    ],
  },
  {
    id: 'payment-ledgers',
    kicker: 'Section 13',
    title: 'Payment Rails & Property Ledgers',
    intro: 'The app should be the source of truth for money movement, approval rules, property-level accounting, and audit history.',
    cards: [
      {
        step: '01',
        icon: 'pi-wallet',
        title: 'Rent Collection Ledger',
        standard: 'Tenant rent should be charged, collected, receipted, and tracked through the owner-controlled tenant portal.',
        detail: 'Rent charges, partial payments, late fees, credits, payment timing, balances, and receipts should flow into a property-level ledger that the owner can audit without depending on PM statements.',
      },
      {
        step: '02',
        icon: 'pi-book',
        title: 'Security Deposit Ledger',
        standard: 'Security deposits should be tracked separately from normal rent and operating income.',
        detail: 'Deposit charges, deposit payments, move-in condition, move-out evidence, deductions, refunds, deadlines, and supporting documentation should be tied together so deposit handling is fair, documented, and legally safer.',
      },
      {
        step: '03',
        icon: 'pi-credit-card',
        title: 'Vendor & PM Payables',
        standard: 'Contractor payouts, vendor invoices, PM fees, and repair payments should be paid only through approved workflows.',
        detail: 'The app should connect invoices, evidence, approvals, payout status, accounting categories, and payment processor records so money is released only when the required conditions are met.',
      },
    ],
  },
  {
    id: 'pm-va-legal-ops',
    kicker: 'Section 14',
    title: 'PM-Supported, VA-Enabled & Legally Escalated Operations',
    intro: 'The system should support property managers at first, but the business should not depend on a traditional PM model forever. The app should allow different operating models by market while keeping the owner in control of money, data, repairs, vendors, documentation, and final decisions.',
    cards: [
      {
        step: '01',
        icon: 'pi-users',
        title: 'PMs and VAs Work Inside the System',
        standard: 'Property managers, virtual assistants, and property coordinators can help communicate, schedule, follow up, organize repairs, track tenant issues, and keep workflows moving, but they do not control the owner’s money, data, documentation, or approval rules.',
        detail: 'The people may change by market, but the operating system stays the same.',
      },
      {
        step: '02',
        icon: 'pi-desktop',
        title: 'Virtual Assistant Operating Layer',
        standard: 'A trained VA or property coordinator should be able to handle much of the administrative work that does not legally require a local property manager.',
        detail: 'Tenant messages, repair request triage, vendor scheduling, document collection, status updates, rent reminder workflows, renewal follow-ups, and PM or vendor accountability tasks can be managed through the app while local inspectors and approved vendors provide field presence when needed.',
      },
      {
        step: '03',
        icon: 'pi-exclamation-triangle',
        title: 'Legal and Eviction Escalation',
        standard: 'Legal issues should move through a documented escalation process instead of being handled casually or inconsistently.',
        detail: 'Late rent, lease violations, notices, habitability concerns, abandonment, eviction preparation, attorney communication, court dates, and legal document history should be tracked inside the system. VAs and PMs can organize information and trigger workflows, but legal decisions, notices, and eviction actions should follow state law and involve qualified legal support when required.',
      },
    ],
  },
  {
    id: 'lender-sourcing-refinance',
    kicker: 'Section 15',
    title: 'Lender Research & Program Intelligence',
    intro: 'Build a trustworthy lender knowledge base that the lead-generation and deal-analysis software can use to test real properties before we pursue or purchase them.',
    cards: [
      {
        step: '01',
        icon: 'pi-envelope',
        title: 'Owner-Led Email Research',
        standard: 'The owner identifies investment-property lending contacts and sends the standardized BRRRR questionnaire during Phase 1.',
        detail: 'Email creates an inexpensive written record, supports document collection, exposes unanswered questions, and lets us improve the process through direct experience with the first 5–10 lenders.',
        hoverTitle: 'Questionnaire Coverage',
        hoverItems: [
          'States served and out-of-state investor restrictions',
          'Maximum LTV based on current appraised after-repair value',
          'Any LTC, cost-basis, or original-purchase-price limitation',
          'Seasoning rules and early-refinance consequences',
          'Rates, terms, points, buydowns, locks, and prepayment penalties',
          'Complete itemized costs and an estimated net sheet',
          'Credit, liquidity, reserves, DTI, DSCR, and preapproval requirements',
          'Rental, property type, condition, value, and loan-size requirements',
          'LLC ownership, personal guarantees, and entity-transfer requirements',
          'Appraisal support, rehab evidence, and reconsideration process',
          'Documents to preserve from acquisition through refinance',
        ],
      },
      {
        step: '02',
        icon: 'pi-sitemap',
        title: 'Lender → Program → Snapshot → Quote',
        standard: 'Keep the lender, each loan program, dated program terms, and property-specific quotes distinct but linkable to leads and analyzed deals.',
        detail: 'Changing terms create a new snapshot instead of overwriting history. Lead analysis uses the applicable verified snapshot first, then replaces preliminary assumptions with an actual property quote when available.',
      },
      {
        step: '03',
        icon: 'pi-verified',
        title: 'Evidence, Answers & Freshness',
        standard: 'Every important term carries its source, received date, verified date, and status; missing answers remain “Unknown / Needs Follow-Up.”',
        detail: 'Preserve emails, guides, rate sheets, fee sheets, estimates, quotes, and attachments. Flag stale terms for reverification and never silently guess financing rules.',
      },
    ],
  },
  {
    id: 'refinance-underwriting-decisions',
    kicker: 'Section 16',
    title: 'Refinance Underwriting & Decision Support',
    intro: 'Connect lender intelligence to lead generation so every serious property can be tested against realistic, recently verified financing options before an offer or purchase.',
    cards: [
      {
        step: '01',
        icon: 'pi-table',
        title: 'Program Comparison & True Net Proceeds',
        standard: 'Compare eligibility, ARV-based LTV versus LTC limits, seasoning, qualification, rates, penalties, and complete closing deductions—not headline LTV alone.',
        detail: 'Calculate the path from gross proceeds through payoff, points, lender and third-party fees, title and settlement costs, government charges, prepaids, and escrows to estimated cash returned.',
      },
      {
        step: '02',
        icon: 'pi-home',
        title: 'Lender Data → Lead Underwriting',
        standard: 'As properties enter the lead pipeline, apply viable program rules so refinance constraints influence pursuit, offer price, rehab planning, and the buy/pass decision.',
        detail: 'Present viable lender programs side-by-side with the metrics investors need to compare: ARV-based LTV and any LTC constraint, seasoning period, estimated rate and term, gross loan amount, itemized closing costs, net cash returned, etc.',
      },
      {
        step: '03',
        icon: 'pi-sliders-h',
        title: 'Recommended Refinance & Stress Tests',
        standard: 'Separate maximum available leverage from the refinance amount that best protects cash flow, reserves, equity, DSCR, and portfolio risk.',
        detail: 'Compare multiple leverage scenarios—including no refinance—and test lower appraisals, lower LTV, higher rates, higher costs, and weaker operating results.',
      },
    ],
  },
  {
    id: 'refinance-learning-scale',
    kicker: 'Section 17',
    title: 'Refinance Outcomes & Scalable Operations',
    intro: 'Feed quotes and actual refinance outcomes back into lender intelligence so future lead screening improves with real operating evidence.',
    cards: [
      {
        step: '01',
        icon: 'pi-chart-line',
        title: 'Predicted vs. Actual Results',
        standard: 'After closing, preserve expected and actual appraisal, LTV, rate, costs, proceeds, timing, payment, cash flow, and DSCR.',
        detail: 'Completed refinances become operating evidence that improves the program assumptions used by lead generation and makes future acquisition underwriting more accurate.',
      },
      {
        step: '02',
        icon: 'pi-star',
        title: 'Lender Performance & Reverification',
        standard: 'Measure which lenders become dependable partners and refresh changing program terms before relying on them for another closing.',
        detail: 'Track closing speed, communication, unexpected requirements, appraisal experience, reliability, and expected-versus-actual terms and proceeds.',
      },
      {
        step: '03',
        icon: 'pi-send',
        title: 'Future Automation & VA Delegation',
        standard: 'Systematize the proven owner workflow before delegating lender discovery, outreach, tracking, document entry, follow-up, and reverification.',
        detail: 'AI may propose extracted answers and draft follow-ups, but the owner approves verified terms and retains property-specific financing decisions.',
      },
    ],
  },
];

function BuyBoxCard({ item }) {
  return (
    <article
      className={`buybox-page-card${item.hoverItems ? ' buybox-page-card--has-hover' : ''}`}
      tabIndex={item.hoverItems ? 0 : undefined}
      aria-label={item.title}
    >
      <span className="buybox-page-card__step">{item.step}</span>
      <div className="buybox-page-card__content">
        <h3 className="buybox-page-card__title">{item.title}</h3>
        <p className="buybox-page-card__standard">
          <strong>{item.standard}</strong>
        </p>
        <p className="buybox-page-card__detail">{item.detail}</p>
      </div>
      <span className="buybox-page-card__icon" aria-hidden="true">
        <i className={`pi ${item.icon}`} />
      </span>
      {item.hoverItems ? (
        <aside className="buybox-page-card__hover-panel" aria-label={item.hoverTitle}>
          <strong>{item.hoverTitle}</strong>
          <ol>
            {item.hoverItems.map((question) => <li key={question}>{question}</li>)}
          </ol>
        </aside>
      ) : null}
    </article>
  );
}

function BuyBoxSection({ section }) {
  return (
    <section className="buybox-page-panel" aria-labelledby={`buybox-${section.id}-title`}>
      <header className="buybox-page-panel__head">
        <span className="buybox-page-panel__kicker">{section.kicker}</span>
        <h2 id={`buybox-${section.id}-title`} className="buybox-page-panel__title">
          {section.title}
        </h2>
        <p className="buybox-page-panel__copy">{section.intro}</p>
      </header>

      <div className="buybox-page-panel__cards">
        {section.cards.map((item) => (
          <BuyBoxCard key={`${section.id}-${item.step}`} item={item} />
        ))}
      </div>
    </section>
  );
}

export default function ResearchBuyBox() {
  return (
    <div className="research-page">
      <div className="research-shell">
        <div className="buybox-page-nav">
          <Link className="buybox-page-back" to="/research">
            <i className="pi pi-arrow-left" />
            Back to Market Research
          </Link>
        </div>

        <ResearchPageHeader
          eyebrow="Strategy Reference"
          title="BRRRR Goal Posts & North Stars"
          description="Master each phase of the BRRRR process.  Create exceptional rentals, incentive programs, advanced software, and exceptional customer service for renters beyond what they are used to. Create systems that allow for scaling while eliminating property managers from the process. Perform a perfect BRRRR someday."
          statsCard={BUYBOX_HEADER_STATS}
        />

        <div className="buybox-page-panels">
          {BUYBOX_SECTIONS.map((section) => (
            <BuyBoxSection key={section.id} section={section} />
          ))}
        </div>

        <section className="buybox-page-callout">
          <div>
            <span className="buybox-page-callout__label">Software context</span>
            <h2 className="buybox-page-callout__title">The strategy page defines the standard. The other tools help prove the standard.</h2>
            <div className="buybox-page-callout__copy">
              {SOFTWARE_CONTEXT_ITEMS.map((item) => (
                <p key={item} className="buybox-page-callout__copy-line">
                  {item}
                </p>
              ))}
            </div>
          </div>
          <div className="buybox-page-callout__actions">
            <Link className="buybox-page-callout__link" to="/research">Open Market Research</Link>
            <Link className="buybox-page-callout__link buybox-page-callout__link--secondary" to="/lead-underwriting">Open Lead Underwriting</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
